// @flow
import React, { useState } from "react";
import dateLeadingZero from "@justpark/helpers/src/dateLeadingZero/dateLeadingZero";
import dayjs from "../../helpers/getDayjsCalendarInstance";
import RightChevronIcon from "../Icons/RightChevronIcon";
import Typography from "../Typography";
import useGetVisibleMonthData from "./useGetVisibleMonthData";
import getCalendarDateData from "./getCalendarDateData";

import styles from "./calendar.module.scss";

type actionType = "prev" | "next";

type CalendarProps = {
  date: string,
  onChange: () => void,
  minDate?: string,
  maxDate?: string
};

type HeaderProps = {
  title: string,
  onMonthChange: (state: actionType) => void
};

type ItemProps = {
  currentMonthData: [],
  firstDayOfMonth: number,
  onChange: () => void,
  selectedDate: string,
  visibleMonth: number,
  visibleYear: number,
  minDate: string,
  maxDate: string
};

const cypress = "calendar";

// eslint-disable-next-line prefer-spread
const weekdays = Array.apply(null, Array(7)).map((_, i) => {
  return dayjs().weekday(i).format("dd");
});

const CalendarMonthHeader = ({ title, onMonthChange }: HeaderProps) => (
  <div className={styles.month}>
    <Typography size="secondary" bold>
      <span data-cy={`${cypress}-title`}>{title}</span>
    </Typography>
    <button
      className={`${styles.prev} `}
      onClick={() => onMonthChange("prev")}
      type="button"
      aria-label="Move backward to switch to the previous month."
      data-cy={`${cypress}-prev-button`}
    >
      <RightChevronIcon />
    </button>
    <button
      className={`${styles.next} `}
      onClick={() => onMonthChange("next")}
      type="button"
      aria-label="Move forward to switch to the next month."
      data-cy={`${cypress}-next-button`}
    >
      <RightChevronIcon />
    </button>
  </div>
);

const CalendarItem = ({
  currentMonthData,
  firstDayOfMonth,
  onChange,
  selectedDate,
  visibleMonth,
  visibleYear,
  minDate,
  maxDate
}: ItemProps) => {
  const {
    currentDay,
    currentMonth,
    currentYear,
    selectedDay,
    selectedMonth,
    selectedYear
  } = getCalendarDateData({
    selectedDate
  });

  const style = { gridColumnStart: firstDayOfMonth };

  return (
    <div className={`CalendarMonth ${styles.days} ${styles.grid}`}>
      {currentMonthData.map((_, index) => {
        const day = index + 1;

        const monthString = dateLeadingZero(visibleMonth + 1);
        const dateString = dateLeadingZero(day);

        const currentDate = `${visibleYear}-${monthString}-${dateString}`;

        const isToday =
          currentDay === day &&
          currentMonth === visibleMonth &&
          currentYear === visibleYear;

        const isSelected =
          selectedDay === day &&
          selectedMonth === visibleMonth &&
          selectedYear === visibleYear;

        const isBeforeMinimum = dayjs(currentDate).isBefore(minDate);
        const isAfterMaximum = dayjs(currentDate).isAfter(maxDate);

        if (isBeforeMinimum || isAfterMaximum) {
          return (
            <div
              key={currentDate}
              className={`${styles.item} ${styles.past} ${
                isSelected ? styles.selected : ""
              }`}
              style={index === 0 ? style : {}}
              data-cy={`${isSelected ? `${cypress}-item-selected` : ""}`}
            >
              {day}
            </div>
          );
        }

        return (
          <button
            key={currentDate}
            type="button"
            className={`CalendarDay ${styles.item} ${
              isToday ? styles.today : ""
            } ${isSelected ? styles.selected : ""}`}
            style={index === 0 ? style : {}}
            onClick={() => onChange(currentDate)}
            data-cy={`${isSelected ? `${cypress}-item-selected` : ""}`}
          >
            <div className={styles.inner}>{day}</div>
          </button>
        );
      })}
    </div>
  );
};

const CalendarWeekDays = () => {
  return (
    <div className={styles.grid}>
      {weekdays.map((weekday) => {
        return (
          <Typography key={weekday} className={styles.weekday} color="light">
            <span data-cy="calendar-weekday">{weekday}</span>
          </Typography>
        );
      })}
    </div>
  );
};

const Calendar = ({ date, onChange, minDate, maxDate }: CalendarProps) => {
  const [visibleMonthInstance, setVisibleMonthInstance] = useState(dayjs(date));

  const {
    title,
    currentMonthData,
    firstDayOfMonth,
    visibleMonth,
    visibleYear,
    onMonthChange
  } = useGetVisibleMonthData({
    visibleMonthInstance,
    setVisibleMonthInstance
  });

  return (
    <div className={styles.calendarContainer} data-cy={cypress}>
      <CalendarMonthHeader title={title} onMonthChange={onMonthChange} />
      <CalendarWeekDays />
      <CalendarItem
        currentMonthData={currentMonthData}
        firstDayOfMonth={firstDayOfMonth}
        onChange={onChange}
        selectedDate={date}
        visibleMonth={visibleMonth}
        visibleYear={visibleYear}
        minDate={minDate}
        maxDate={maxDate}
      />
    </div>
  );
};

Calendar.defaultProps = {
  minDate: null,
  maxDate: null
};

export default Calendar;
